import { gql } from 'graphql-tag';

// =============================================================================
// Family — GraphQL operations against the Nexus backend
// =============================================================================
// Portfolio-native family tree system. See:
//   _bmad-output/planning-artifacts/architecture-family.md
//   _bmad-output/planning-artifacts/prd-family.md
//
// Authenticated queries/mutations: editor flow from the Portfolio Family tab
// Public query: `familyViewByShareToken` for view-only share links
// =============================================================================

// ------- Reusable fragments -------

const FAMILY_FIELDS = gql`
  fragment FamilyFields on Family {
    id
    createdByUserId
    name
    description
    connectionType
    hierarchyType
    createdAt
    updatedAt
  }
`;

const FAMILY_MEMBER_FIELDS = gql`
  fragment FamilyMemberFields on FamilyMember {
    id
    familyId
    linkedUserId
    nameEn
    nameZh
    nickname
    birthDate
    deathDate
    birthYear
    deathYear
    generationName
    hometownPlaceId
    hometown
    maternalPaternalLine
    bio
    isAdopted
    isDeceased
    avatarFaceCropId
    publicDisplayOverride
    isMaskedOnPublicView
    siblingOrder
    colorTag
    createdByUserId
    createdAt
    updatedAt
  }
`;

const FAMILY_RELATIONSHIP_FIELDS = gql`
  fragment FamilyRelationshipFields on FamilyRelationship {
    id
    familyId
    fromMemberId
    toMemberId
    type
    createdByUserId
    createdAt
  }
`;

const FAMILY_PHOTO_FIELDS = gql`
  fragment FamilyPhotoFields on FamilyPhoto {
    id
    familyId
    s3Key
    s3PublicUrl
    captionEn
    captionZh
    takenAtGuess
    uploadedByUserId
    originalFilename
    width
    height
    createdAt
  }
`;

const FAMILY_FACE_CROP_FIELDS = gql`
  fragment FamilyFaceCropFields on FamilyFaceCrop {
    id
    familyId
    photoId
    memberId
    s3Key
    s3PublicUrl
    bboxXRatio
    bboxYRatio
    bboxWRatio
    bboxHRatio
    createdByUserId
    createdAt
  }
`;

const FAMILY_VIEW_FIELDS = gql`
  fragment FamilyViewFields on FamilyView {
    id
    familyId
    shareToken
    rootMemberId
    upDepth
    downDepth
    includeSpouses
    titleEn
    titleZh
    duplicatedFromViewId
    createdByUserId
    createdAt
    updatedAt
  }
`;

// ------- Queries -------

export const MY_FAMILIES_QUERY = gql`
  query MyFamilies {
    myFamilies {
      ...FamilyFields
    }
  }
  ${FAMILY_FIELDS}
`;

export const FAMILY_QUERY = gql`
  query Family($id: ID!) {
    family(id: $id) {
      ...FamilyFields
    }
  }
  ${FAMILY_FIELDS}
`;

export const FAMILY_MEMBERS_QUERY = gql`
  query FamilyMembers($familyId: ID!) {
    familyMembers(familyId: $familyId) {
      ...FamilyMemberFields
    }
  }
  ${FAMILY_MEMBER_FIELDS}
`;

export const FAMILY_RELATIONSHIPS_QUERY = gql`
  query FamilyRelationships($familyId: ID!) {
    familyRelationships(familyId: $familyId) {
      ...FamilyRelationshipFields
    }
  }
  ${FAMILY_RELATIONSHIP_FIELDS}
`;

export const FAMILY_SEARCH_MEMBERS_QUERY = gql`
  query FamilySearchMembers($familyId: ID!, $q: String!, $limit: Int) {
    familySearchMembers(familyId: $familyId, q: $q, limit: $limit) {
      ...FamilyMemberFields
    }
  }
  ${FAMILY_MEMBER_FIELDS}
`;

export const FAMILY_PHOTOS_QUERY = gql`
  query FamilyPhotos($familyId: ID!) {
    familyPhotos(familyId: $familyId) {
      ...FamilyPhotoFields
    }
  }
  ${FAMILY_PHOTO_FIELDS}
`;

export const FAMILY_MEMBER_PHOTOS_QUERY = gql`
  query FamilyMemberPhotos($memberId: ID!) {
    familyMemberPhotos(memberId: $memberId) {
      ...FamilyPhotoFields
    }
  }
  ${FAMILY_PHOTO_FIELDS}
`;

export const FAMILY_SUBGRAPH_QUERY = gql`
  query FamilySubgraph($familyId: ID!) {
    familySubgraph(familyId: $familyId) {
      familyName
      viewerCanEdit
      view {
        ...FamilyViewFields
      }
      members {
        ...FamilyMemberFields
      }
      relationships {
        ...FamilyRelationshipFields
      }
      photos {
        ...FamilyPhotoFields
      }
      faceCrops {
        ...FamilyFaceCropFields
      }
    }
  }
  ${FAMILY_VIEW_FIELDS}
  ${FAMILY_MEMBER_FIELDS}
  ${FAMILY_RELATIONSHIP_FIELDS}
  ${FAMILY_PHOTO_FIELDS}
  ${FAMILY_FACE_CROP_FIELDS}
`;

export const FAMILY_VIEW_BY_SHARE_TOKEN_QUERY = gql`
  query FamilyViewByShareToken($token: String!) {
    familyViewByShareToken(token: $token) {
      familyName
      viewerCanEdit
      view {
        ...FamilyViewFields
      }
      members {
        ...FamilyMemberFields
      }
      relationships {
        ...FamilyRelationshipFields
      }
      photos {
        ...FamilyPhotoFields
      }
      faceCrops {
        ...FamilyFaceCropFields
      }
    }
  }
  ${FAMILY_VIEW_FIELDS}
  ${FAMILY_MEMBER_FIELDS}
  ${FAMILY_RELATIONSHIP_FIELDS}
  ${FAMILY_PHOTO_FIELDS}
  ${FAMILY_FACE_CROP_FIELDS}
`;

// ------- Mutations: Family -------

export const FAMILY_CREATE_MUTATION = gql`
  mutation FamilyCreate($input: CreateFamilyInput!) {
    familyCreate(input: $input) {
      ...FamilyFields
    }
  }
  ${FAMILY_FIELDS}
`;

export const FAMILY_UPDATE_MUTATION = gql`
  mutation FamilyUpdate($id: ID!, $name: String, $description: String) {
    familyUpdate(id: $id, name: $name, description: $description) {
      ...FamilyFields
    }
  }
  ${FAMILY_FIELDS}
`;

export const FAMILY_DELETE_MUTATION = gql`
  mutation FamilyDelete($id: ID!) {
    familyDelete(id: $id)
  }
`;

export const FAMILY_DUPLICATE_MUTATION = gql`
  mutation FamilyDuplicate($familyId: ID!) {
    familyDuplicate(familyId: $familyId) {
      ...FamilyFields
    }
  }
  ${FAMILY_FIELDS}
`;

// ------- Mutations: Member -------

export const FAMILY_ADD_MEMBER_MUTATION = gql`
  mutation FamilyAddMember($input: AddFamilyMemberInput!) {
    familyAddMember(input: $input) {
      ...FamilyMemberFields
    }
  }
  ${FAMILY_MEMBER_FIELDS}
`;

export const FAMILY_UPDATE_MEMBER_MUTATION = gql`
  mutation FamilyUpdateMember($id: ID!, $input: UpdateFamilyMemberInput!) {
    familyUpdateMember(id: $id, input: $input) {
      ...FamilyMemberFields
    }
  }
  ${FAMILY_MEMBER_FIELDS}
`;

export const FAMILY_DELETE_MEMBER_MUTATION = gql`
  mutation FamilyDeleteMember($id: ID!) {
    familyDeleteMember(id: $id)
  }
`;

export const FAMILY_SET_MEMBER_AVATAR_MUTATION = gql`
  mutation FamilySetMemberAvatar($memberId: ID!, $faceCropId: ID!) {
    familySetMemberAvatar(memberId: $memberId, faceCropId: $faceCropId) {
      ...FamilyMemberFields
    }
  }
  ${FAMILY_MEMBER_FIELDS}
`;

export const FAMILY_CLEAR_MEMBER_AVATAR_MUTATION = gql`
  mutation FamilyClearMemberAvatar($memberId: ID!) {
    familyClearMemberAvatar(memberId: $memberId) {
      ...FamilyMemberFields
    }
  }
  ${FAMILY_MEMBER_FIELDS}
`;

export const FAMILY_CLAIM_MEMBER_MUTATION = gql`
  mutation FamilyClaimMember($memberId: ID!) {
    familyClaimMember(memberId: $memberId) {
      ...FamilyMemberFields
    }
  }
  ${FAMILY_MEMBER_FIELDS}
`;

export const FAMILY_UNCLAIM_MEMBER_MUTATION = gql`
  mutation FamilyUnclaimMember($memberId: ID!) {
    familyUnclaimMember(memberId: $memberId) {
      ...FamilyMemberFields
    }
  }
  ${FAMILY_MEMBER_FIELDS}
`;

// ------- Mutations: Relationship -------

export const FAMILY_CREATE_RELATIONSHIP_MUTATION = gql`
  mutation FamilyCreateRelationship($input: CreateFamilyRelationshipInput!) {
    familyCreateRelationship(input: $input) {
      ...FamilyRelationshipFields
    }
  }
  ${FAMILY_RELATIONSHIP_FIELDS}
`;

export const FAMILY_DELETE_RELATIONSHIP_MUTATION = gql`
  mutation FamilyDeleteRelationship($id: ID!) {
    familyDeleteRelationship(id: $id)
  }
`;

// ------- Mutations: Photo & FaceCrop -------

export const FAMILY_CREATE_FACE_CROP_MUTATION = gql`
  mutation FamilyCreateFaceCrop($input: CreateFamilyFaceCropInput!) {
    familyCreateFaceCrop(input: $input) {
      ...FamilyFaceCropFields
    }
  }
  ${FAMILY_FACE_CROP_FIELDS}
`;

export const FAMILY_ASSOCIATE_FACE_TO_MEMBER_MUTATION = gql`
  mutation FamilyAssociateFaceToMember($faceCropId: ID!, $memberId: ID!) {
    familyAssociateFaceToMember(faceCropId: $faceCropId, memberId: $memberId) {
      ...FamilyFaceCropFields
    }
  }
  ${FAMILY_FACE_CROP_FIELDS}
`;

export const FAMILY_DELETE_PHOTO_MUTATION = gql`
  mutation FamilyDeletePhoto($id: ID!) {
    familyDeletePhoto(id: $id)
  }
`;

// NOTE: photo BINARY uploads go through the REST endpoint
// POST /api/family/photos/:familyId (not a GraphQL mutation — see architecture doc)

// ------- Mutations: View -------

export const FAMILY_CREATE_VIEW_MUTATION = gql`
  mutation FamilyCreateView($input: CreateFamilyViewInput!) {
    familyCreateView(input: $input) {
      ...FamilyViewFields
    }
  }
  ${FAMILY_VIEW_FIELDS}
`;

export const FAMILY_UPDATE_VIEW_MUTATION = gql`
  mutation FamilyUpdateView($id: ID!, $input: UpdateFamilyViewInput!) {
    familyUpdateView(id: $id, input: $input) {
      ...FamilyViewFields
    }
  }
  ${FAMILY_VIEW_FIELDS}
`;
